import React, { useState, useEffect, useMemo, useRef } from "react";
import Select, { components } from "react-select";
import {
  fetchOptionsForWorkerAndParticpant,
  getNameProfile,
} from "Utils/utils";
import "components/common/Select/Select.css";
import { useSelector } from "react-redux";
import { getSpRosteringSetting } from "store/selector/sp.selector";

// WorkerShiftCustomSelect component
const WorkerShiftCustomSelect = ({
  cssClass,
  isDisabled,
  selectedValue,
  setValue,
  name,
  clearErrors,
  errorMessage,
  isValueChanges,
  isMultiple,
  selectFor = "participant",
  defaultOptions,
  isClearable = true,
  isOptionRefreshed,
  isClearCall,
  loading,
  serviceTypeSelectedOptions,
  calendarView,
  /** When selectedValue is not yet in options (e.g. shift-request View), show this. */
  selectedFallbackOption = null,
}) => {

  const [options, setOptions] = useState([]);
  const spRosteringSetting=useSelector(getSpRosteringSetting)
  
  // const [isLoading, setIsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const selectOptions = useMemo(() => {
    if (
      defaultOptions != null &&
      (!Array.isArray(defaultOptions) || defaultOptions.length > 0)
    ) {
      return defaultOptions;
    }
    return options;
  }, [defaultOptions, options]);

  // Stable key so Service Support object identity changes do not re-hit participants-list
  const serviceSupportKey = useMemo(() => {
    if (calendarView !== "participant-view" || selectFor !== "participant") {
      return "";
    }
    const list = Array.isArray(serviceTypeSelectedOptions)
      ? serviceTypeSelectedOptions
      : serviceTypeSelectedOptions
        ? [serviceTypeSelectedOptions]
        : [];
    return list
      .map((item) => item?.value)
      .filter(Boolean)
      .sort()
      .join(",");
  }, [calendarView, selectFor, serviceTypeSelectedOptions]);

  const lastFetchedKeyRef = useRef("");
  const hasParentOptions =
    Array.isArray(defaultOptions) && defaultOptions.length > 0;

  useEffect(() => {
    let isMounted = true;

    // Prefer options already provided by parent — avoid a second participants-list call
    if (hasParentOptions) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchKey = `${selectFor}|${calendarView || ""}|${serviceSupportKey}`;
    // Same payload already fetched — skip
    if (lastFetchedKeyRef.current === fetchKey) {
      return () => {
        isMounted = false;
      };
    }
    // Mark before await so rapid effect re-runs don't double-hit
    lastFetchedKeyRef.current = fetchKey;

    const loadOptions = async () => {
      try {
        setIsLoading(true);
        const serviceSupportFilter =
          calendarView === "participant-view" && serviceSupportKey
            ? serviceSupportKey.split(",").map((value) => ({ value }))
            : [];

        const fetchedOptions = await fetchOptionsForWorkerAndParticpant(
          selectFor,
          serviceSupportFilter,
        );

        if (isMounted) {
          let sortedOptions = fetchedOptions;
          if (selectFor === "participant" || selectFor === "worker") {
            sortedOptions = [...fetchedOptions].sort((a, b) => {
              const aLabel = a.label || `${a.first_name || ''} ${a.last_name || ''}`.trim();
              const bLabel = b.label || `${b.first_name || ''} ${b.last_name || ''}`.trim();
              return aLabel.localeCompare(bLabel);
            });
          }
          setOptions(sortedOptions);
          setIsLoading(false);
        }
      } catch (error) {
        if (isMounted) {
          lastFetchedKeyRef.current = "";
          setIsLoading(false);
        }
      }
    };

    loadOptions();

    return () => {
      isMounted = false;
    };
    // Intentionally omit defaultOptions object identity — use hasParentOptions only
  }, [selectFor, calendarView, serviceSupportKey, hasParentOptions]);
  // Separate effect to call isOptionRefreshed after options are updated
  // This prevents calling setState during render
  useEffect(() => {
    if (isOptionRefreshed && options.length > 0 && defaultOptions === null) {
      // Use a ref to track if we've already notified about these options
      // This prevents unnecessary re-renders
      const currentOptions = options;
      
      // Use requestAnimationFrame to ensure this runs after render cycle
      const frameId = requestAnimationFrame(() => {
        if (isOptionRefreshed) {
          isOptionRefreshed(currentOptions);
        }
      });
      
      return () => cancelAnimationFrame(frameId);
    }
  }, [options.length, defaultOptions]); // Only depend on options.length to avoid calling on every option change

  const getIcon = (type) => {
    // console.log("here");
    return type;
  };

  const handleImageError = (e, fname, lname) => {
    // color=fff
    return (e.target.src = getNameProfile(fname, lname, "0D8ABC"));
  };

  const CustomSelectValue = (csprops) => {
    return (
      <components.SingleValue {...csprops}>
        <div className='flex items-center capitalize'>
          {csprops.data.label && csprops.data?.icon && (
            <img
              onError={(e) => handleImageError(e, csprops.data.label, " ")}
              className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
              src={getIcon(csprops.data.icon || csprops.data.value)}
              alt=''
            />
          )}
          {csprops.data.label}
        </div>
      </components.SingleValue>
    );
  };

  const CustomOption = (props) => {
    const { data } = props;
    return (
      <components.Option {...props}>
        <div className='flex items-center capitalize'>
          {data.label && data?.icon && (
            <img
              className='w-7 h-7 bg-gray-300 rounded-full flex-shrink-0 object-cover mr-2'
              src={getIcon(data.icon || data.value)}
              alt=''
            />
          )}
          {data.label}
        </div>
      </components.Option>
    );
  };

  // Close when the modal/page scrolls so the portaled menu cannot detach from the control.
  // Keep the menu open while the user scrolls inside the options list itself.
  const closeMenuOnScroll = (event) => {
    const target = event?.target;
    if (
      target instanceof Element &&
      target.closest(".worker-shift-select__menu")
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      <Select
        components={{
          Option: CustomOption,
          SingleValue: CustomSelectValue,
        }}
        classNamePrefix='worker-shift-select'
        className={`w-100 roleSelector scrollOptions capitalize ${cssClass}`}
        isMulti={isMultiple}
        options={selectOptions}
        placeholder='Search...'
        noOptionsMessage={() => "No options"}
        menuPlacement='auto'
        // Must be above WorkerShiftModal (z-[10000]) or the menu opens behind it
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 10050 }),
          menu: (base) => ({ ...base, zIndex: 10050 }),
        }}
        menuPortalTarget={document.body}
        closeMenuOnScroll={closeMenuOnScroll}
        isLoading={isLoading || loading}
        onChange={(selectedOption) => {
          if (selectedOption === null && isClearCall) {
            isClearCall();
          }

          if (setValue && name) {
            setValue(name, selectedOption?.value);
            if (clearErrors && errorMessage && name) {
              clearErrors(name, "");
            }
            if (typeof isValueChanges === "function") {
              isValueChanges(true, selectedOption);
            }
          }
        }}
        value={(() => {
          if (!selectedValue) return null;
          const found = selectOptions.find(
            (x) =>
              x.value === selectedValue ||
              x.uuid === selectedValue ||
              // Add Shift may store numeric worker id after listing API
              x.id === selectedValue ||
              String(x.id) === String(selectedValue) ||
              String(x.value) === String(selectedValue) ||
              String(x.uuid) === String(selectedValue)
          );
          if (found) return found;
          const fallback = selectedFallbackOption;
          if (
            fallback &&
            (fallback.value === selectedValue ||
              fallback.uuid === selectedValue ||
              String(fallback.value) === String(selectedValue) ||
              String(fallback.uuid) === String(selectedValue))
          ) {
            return fallback;
          }
          return null;
        })()}
        isClearable={isClearable}
        isDisabled={isDisabled}
      />
      {errorMessage && (
        <span
          style={{
            fontSize: "13px",
            color: "red",
          }}
          className='capitalize'
        >
          {errorMessage}
        </span>
      )}
    </>
  );
};

export default WorkerShiftCustomSelect;
